import { useEffect, useState } from 'react';

const visibleNow = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

/**
 * True while the page is on screen as far as the browser is concerned: the tab
 * is foreground and the page is not being put into the back-forward cache.
 * Browsers pause media on those transitions and do not resume it on return,
 * so anything that plays media must re-run its play logic when this flips back
 * to true. Follows `visibilitychange`, `pagehide` (false) and `pageshow`.
 * SSR-safe: with no document it reports true.
 */
export default function usePageShown(): boolean {
  const [shown, setShown] = useState<boolean>(visibleNow);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
    const sync = () => setShown(visibleNow());
    const hide = () => setShown(false);
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('pageshow', sync);
    window.addEventListener('pagehide', hide);
    sync();
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('pageshow', sync);
      window.removeEventListener('pagehide', hide);
    };
  }, []);

  return shown;
}
